import { useCallback, useState } from 'react';
import { queryParamToString } from '@moda/portal-stanchions';
import { AuthError, ERROR_MESSAGES } from '../../lib/authenticate';
import {
  confirmPasswordlessMagicLink,
  requestPasswordlessLogin,
  resendPasswordlessEmail,
  verifyPasswordlessOtp
} from '../../lib/passwordless';
import { CONFIG } from '../../config';
import { useCookies } from '../useCookies';
import { useSessionQueryParams } from '../useSessionQueryParams';
import { useCompleteAuth } from '../useAuth/useCompleteAuth';

type PasswordlessRequestArgs = {
  email: string;
};

type PasswordlessVerifyArgs = {
  email: string;
  otp: string;
  remember?: boolean;
};

type PasswordlessConfirmArgs = {
  emailSha: string;
  uid: string;
  remember?: boolean;
};

const normalizePasswordlessError = (error: unknown): AuthError => {
  if (error instanceof Error) {
    return {
      code: 'default',
      message: error.message || ERROR_MESSAGES.default
    };
  }

  if (error && typeof error === 'object' && 'code' in error) {
    return error as AuthError;
  }

  return {
    code: 'default',
    message: ERROR_MESSAGES.default
  };
};

export const usePasswordlessAuth = () => {
  const [error, setError] = useState<AuthError>();
  const [loading, setLoading] = useState(false);
  const completeAuth = useCompleteAuth();
  const {
    cookies: { cartId }
  } = useCookies();
  const {
    params: { bypass_rack_attack: bypassRackAttackQueryParameter }
  } = useSessionQueryParams();

  const bypassRackAttack =
    queryParamToString(bypassRackAttackQueryParameter) || CONFIG.BYPASS_RACK_ATTACK;

  const runRequest = useCallback(async <TResult>(callback: () => Promise<TResult>) => {
    setLoading(true);
    setError(undefined);

    try {
      const result = await callback();
      setLoading(false);
      return result;
    } catch (error) {
      const normalizedError = normalizePasswordlessError(error);
      setError(normalizedError);
      setLoading(false);
      throw normalizedError;
    }
  }, []);

  const clearError = useCallback(() => setError(undefined), []);

  const requestLogin = useCallback(
    ({ email }: PasswordlessRequestArgs) =>
      runRequest(() => requestPasswordlessLogin({ email, bypassRackAttack })).then(
        response => response.message
      ),
    [bypassRackAttack, runRequest]
  );

  const resendEmail = useCallback(
    ({ email }: PasswordlessRequestArgs) =>
      runRequest(() => resendPasswordlessEmail({ email, bypassRackAttack })).then(
        response => response.message
      ),
    [bypassRackAttack, runRequest]
  );

  const verifyOtp = useCallback(
    ({ email, otp, remember }: PasswordlessVerifyArgs) =>
      runRequest(async () => {
        const user = await verifyPasswordlessOtp({ email, otp, cartId, bypassRackAttack });
        return completeAuth({ user, remember });
      }),
    [bypassRackAttack, cartId, completeAuth, runRequest]
  );

  const confirmMagicLink = useCallback(
    ({ emailSha, uid, remember }: PasswordlessConfirmArgs) =>
      runRequest(async () => {
        const user = await confirmPasswordlessMagicLink({
          emailSha,
          uid,
          cartId,
          bypassRackAttack
        });

        return completeAuth({ user, remember });
      }),
    [bypassRackAttack, cartId, completeAuth, runRequest]
  );

  return {
    clearError,
    confirmMagicLink,
    error,
    loading,
    requestLogin,
    resendEmail,
    verifyOtp
  };
};
